/**
 * Parameter Dropdown Options Hook
 *
 * Extends shared-parameter-file options with the parameters that already exist in
 * the active Revit project — built-in parameters (e.g. "Insulation Type") and
 * previously created project/family parameters — so dropdowns offer every name
 * the parameter setup can work with. Free-typed names remain possible through
 * the dropdown itself.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCompleteParameterOptions } from '../../../shared/hooks/useCompleteParameterOptions';
import {
  fetchAvailableProjectParameters,
  type AvailableProjectParameterDescriptor,
} from '../../../shared/lib/hooks/useParameterAddition';
import { readAvailableProjectParameterCache } from '../../../shared/lib/hooks/parameterAddition/availableProjectParameterCache';
import { parseSharedParameterDataType } from '../../../shared/lib/revit/sharedParameterDataType';
import { formatParameterSource } from '../../../shared/lib/revit/parameterSource';
import type { ParameterOption } from '../../../shared/types/sharedParameter.types';

export const REVIT_PROJECT_PARAMETER_OPTION_GROUP = 'Revit project';

export function toRevitProjectParameterOption(
  descriptor: AvailableProjectParameterDescriptor
): ParameterOption {
  const dataType = parseSharedParameterDataType(descriptor.dataType);
  const sourceText = formatParameterSource(descriptor.source);
  return {
    value: descriptor.name,
    label: descriptor.name,
    group: REVIT_PROJECT_PARAMETER_OPTION_GROUP,
    description: `Existing ${descriptor.parameterType.toLowerCase()} parameter in the active Revit project${sourceText ? ` — ${sourceText}` : ''}; selecting it avoids creating a duplicate.`,
    // Host reports the same DATA token vocabulary the shared parameter file uses.
    ...(dataType ? { dataType } : {}),
  };
}

/**
 * Shared-file options win over project options with the same name so their
 * richer metadata (group, data type) is preserved — but when the parameter
 * already exists in the project, the host-reported description carries the
 * true definition origin, so it replaces the shared-file caption text.
 */
export function mergeParameterDropdownOptions(
  sharedOptions: readonly ParameterOption[],
  projectOptions: readonly ParameterOption[]
): ParameterOption[] {
  const sharedByName = new Map(
    sharedOptions.map((option) => [option.value.trim().toLowerCase(), option] as const)
  );
  const seenNames = new Set(sharedByName.keys());
  const projectOnlyOptions: ParameterOption[] = [];
  projectOptions.forEach((option) => {
    const normalizedName = option.value.trim().toLowerCase();
    const sharedOption = sharedByName.get(normalizedName);
    if (sharedOption) {
      if (option.description) {
        sharedByName.set(normalizedName, { ...sharedOption, description: option.description });
      }
      return;
    }

    if (seenNames.has(normalizedName)) {
      return;
    }

    seenNames.add(normalizedName);
    projectOnlyOptions.push(option);
  });
  return [
    ...sharedOptions.map(
      (option) => sharedByName.get(option.value.trim().toLowerCase()) ?? option
    ),
    ...projectOnlyOptions,
  ];
}

function areOptionListsEqual(a: readonly ParameterOption[], b: readonly ParameterOption[]): boolean {
  return a.length === b.length && a.every((option, index) => option.value === b[index].value);
}

interface ParameterDropdownOptionsBehavior {
  loadOnMount?: boolean;
}

function readCachedOptions(cacheScopeKey?: string | null): ParameterOption[] {
  return (readAvailableProjectParameterCache(cacheScopeKey) ?? []).map(
    toRevitProjectParameterOption
  );
}

export function useParameterDropdownOptions(
  cacheScopeKey?: string | null,
  { loadOnMount = true }: ParameterDropdownOptionsBehavior = {}
) {
  const complete = useCompleteParameterOptions({ enabled: loadOnMount });
  const [availableOptions, setAvailableOptions] = useState<ParameterOption[]>(() =>
    loadOnMount ? [] : readCachedOptions(cacheScopeKey)
  );
  const [isLoadingAvailableOptions, setIsLoadingAvailableOptions] = useState(false);
  const requestSequence = useRef(0);

  const loadAvailableOptions = useCallback(async (force = false) => {
    const requestId = ++requestSequence.current;
    setIsLoadingAvailableOptions(true);
    try {
      const descriptors = await fetchAvailableProjectParameters({ cacheScopeKey, force });
      if (requestId !== requestSequence.current) {
        return;
      }
      const next = descriptors.map(toRevitProjectParameterOption);
      setAvailableOptions((previous) =>
        areOptionListsEqual(previous, next) ? previous : next
      );
    } catch {
      // The project parameter list is a convenience; free-typed names still work.
    } finally {
      if (requestId === requestSequence.current) {
        setIsLoadingAvailableOptions(false);
      }
    }
  }, [cacheScopeKey]);

  useEffect(() => {
    requestSequence.current += 1;
    const cached = loadOnMount ? [] : readCachedOptions(cacheScopeKey);
    setAvailableOptions((previous) =>
      areOptionListsEqual(previous, cached) ? previous : cached
    );
    setIsLoadingAvailableOptions(false);
    return () => {
      requestSequence.current += 1;
    };
  }, [cacheScopeKey, loadAvailableOptions, loadOnMount]);

  const options = useMemo(
    () => loadOnMount
      ? complete.options
      : mergeParameterDropdownOptions(complete.sharedOptions, availableOptions),
    [availableOptions, complete.options, complete.sharedOptions, loadOnMount]
  );

  return {
    options,
    availableOptions: loadOnMount ? complete.options : availableOptions,
    isLoadingAvailableOptions: loadOnMount ? complete.isLoading : isLoadingAvailableOptions,
    loadAvailableOptions,
    isLoading: complete.isLoading,
    error: complete.error,
    isParsed: complete.isParsed,
    loadFileContent: complete.loadFileContent,
    loadDefaultParameters: complete.loadDefaultParameters,
  };
}

export default useParameterDropdownOptions;
